import React from 'react';
import { useImmer } from 'use-immer';
import { Row, Col } from 'react-bootstrap';
import { useMutation } from '@apollo/client';
import {
  Modal,
  ModalTitle,
  ModalContent,
  ModalFooter,
  TextInput,
  TextArea,
} from '@utahdts/utah-design-system';
import { SET_VARIABILITY_FACTOR } from '../../../../../queries/engineeringQrys';
import { numbersUpToTwoDecimalPlaces } from '../../../../reuseableComponents/forms/formValidation';
import { formatNumber } from '../../dwriUtils';
import '../../dwri.css';

const initialFormState = {
  adjustedValue: '',
  adjustedValueError: '',
  justification: '',
  justificationError: '',
};

const DwriVariabilityFactorTable = ({ type, label, pwsId, years = [], factor, onSaved }) => {

  const [editRow, setEditRow] = React.useState(null);
  const [formState, setFormState] = useImmer(initialFormState);
  const [mutationError, setMutationError] = React.useState('');

  const user = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : { isPublic: true };
  const canEdit = user?.permissions?.includes('edit_water_use_system');

  const reportedValueDisplay = factor?.variability === 0 ? '' : formatNumber(factor?.variability);

  const [setVariabilityFactor] = useMutation(SET_VARIABILITY_FACTOR);

  function handleEditClick() {
    setEditRow(factor);
    setFormState({
      ...initialFormState,
      adjustedValue: factor?.adjustedValue == null ? '' : String(factor.adjustedValue),
      justification: factor?.justification ?? '',
    });
    setMutationError('');
  }

  function handleModalClose() {
    setEditRow(null);
    setFormState(initialFormState);
    setMutationError('');
  }

  function handleAdjustedValueChange(event) {
    const nextValue = event.target.value;
    const validationResult = numbersUpToTwoDecimalPlaces(nextValue);
    if (validationResult === true) {
      setFormState((draft) => {
        draft.adjustedValue = nextValue;
        draft.adjustedValueError = '';
      });
    } else {
      setFormState((draft) => { draft.adjustedValueError = validationResult; });
    }
  }

  function handleAdjustedValueBlur() {
    const validationResult = numbersUpToTwoDecimalPlaces(formState.adjustedValue);
    setFormState((draft) => {
      draft.adjustedValueError = validationResult === true ? '' : validationResult;
    });
  }

  function isAdjustedValueChanged() {
    if (!editRow) {
      return false;
    }
    const previousValue = editRow.adjustedValue == null ? '' : String(editRow.adjustedValue);
    return formState.adjustedValue !== previousValue;
  }

  async function handleSave() {
    let validationError = false;

    const valueResult = numbersUpToTwoDecimalPlaces(formState.adjustedValue);
    if (formState.adjustedValue !== '' && valueResult !== true) {
      setFormState((draft) => { draft.adjustedValueError = valueResult; });
      validationError = true;
    }

    if (formState.adjustedValue !== '' && !formState.justification.trim()) {
      setFormState((draft) => { draft.justificationError = `Change Justification is required when Adjusted ${label} is changed.`; });
      validationError = true;
    }

    if (validationError) {
      return;
    }

    setMutationError('');
    try {
      const response = await setVariabilityFactor({
        variables: {
          updateVariabilityFactorInput: {
            type,
            pwsId,
            years,
            adjustedValue: formState.adjustedValue === '' ? null : Number(formState.adjustedValue),
            justification: formState.justification,
          },
        },
        errorPolicy: 'all',
      });

      const responseErrorMessage = response?.errors?.[0]?.message;
      if (responseErrorMessage) {
        setMutationError(responseErrorMessage);
        return;
      }

      if (!response?.data?.updateVariabilityFactor) {
        setMutationError(`Failed to update ${label}.`);
        return;
      }

      onSaved();
      handleModalClose();
    } catch (mutationErr) {
      setMutationError(mutationErr?.message || `Failed to update ${label}.`);
    }
  }

  return (
    <>
      <div className="table-responsive">
        <Row className="dwri-service-connections-header gx-0">
          <Col className="dwri-table-header-cell"><span><b>{label}</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Adjusted {label}</b></span></Col>
          <Col className="dwri-table-header-cell"><span><b>Change Justification</b></span></Col>
          {canEdit && (
            <Col className="dwri-table-header-cell dwri-col-edit"><span><b>Edit</b></span></Col>
          )}
        </Row>
        <Row className="dwri-service-connections-row gx-0 is-even">
          <Col className="dwri-table-cell"><span>{reportedValueDisplay}</span></Col>
          <Col className="dwri-table-cell"><span>{formatNumber(factor?.adjustedValue)}</span></Col>
          <Col className="dwri-table-cell"><span>{factor?.justification}</span></Col>
          {canEdit && (
            <Col className="dwri-table-cell dwri-col-edit">
              <button
                className="link-button"
                aria-label={`Edit ${label}`}
                type="button"
                onClick={handleEditClick}
              >
                Edit
              </button>
            </Col>
          )}
        </Row>
      </div>
      {editRow && (
        <Modal onClose={handleModalClose} onEscape={handleModalClose}>
          <ModalTitle>Update System Variability Factor: {label}</ModalTitle>
          <ModalContent>
            <div className="dwri-peak-day-demand-modal-grid">
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--two">
                <TextInput label={label} value={reportedValueDisplay} isDisabled />
                <TextInput
                  label={`Adjusted ${label}`}
                  value={formState.adjustedValue}
                  onChange={handleAdjustedValueChange}
                  onBlur={handleAdjustedValueBlur}
                  errorMessage={formState.adjustedValueError || undefined}
                />
              </div>
              <div className="dwri-peak-day-demand-modal-row dwri-peak-day-demand-modal-row--full">
                <TextArea
                  label="Change Justification"
                  value={formState.justification}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setFormState((draft) => {
                      draft.justification = nextValue;
                      draft.justificationError = '';
                    });
                  }}
                  isRequired={isAdjustedValueChanged()}
                  errorMessage={formState.justificationError || undefined}
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter className="dwri-service-modal-footer">
            {mutationError && (
              <div className="text-danger">{mutationError}</div>
            )}
            <button type="button" onClick={handleModalClose}>Close</button>
            <button type="button" onClick={handleSave}>Save</button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default DwriVariabilityFactorTable;
